import React, {useState, useEffect, useRef} from 'react';
import { motion } from "framer-motion";
import './ProductConfigurator.css';
import { log, error, warn, info, debug } from '../../utils/consoleLogger';

const databaseType = {
  shopify : "shopify",
  wooCommerce : "woocommerce",
  test: "test",
};

//Need a structure that tells us what sku is called depending on the value of several fields
//Configurator sets those fields and should return an SKU
//In woocommerce, each variation has details. In shopify, it's similar. That means we need to look at all the variants for a product SKU
//Then, for each of those variants, we need to get the details. We then need to build a map, where each variants has a corresponding
//struct that contains the information we need.
const ProductConfigurator = (
  {
    productS3BucketUrl, 
    productFolder,
    cloudFrontUrl,
    productVariants,
    shopDatabaseType,
    productTags,
    productSettings,
    setNewSKU,
    onClose,
    meshClicked,
    isInteractive = true,
    productVariantsAttr,
    setVariantFoldersNames,
  }
) => {
  const [loading, setLoading] = useState(true);
  const [productVariantsDetails, setProductVariantsDetails] = useState(null);
  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [selectedValue, setSelectedValue] = useState({});
  const [keyForceUpdate, setKeyForceUpdate] = useState(0);
  const [selectedAttributesForPanel, setSelectedAttributesForPanel] = useState([]);
  const [showSidePanel, setShowSidePanel] = useState(false);

  //This is what we get from woocommerce
  /*
  attributes: {pa_attribute_fabric: 'oat'}
  dimensions: "N/A"
  price: "2500"
  regular_price: "2500"
  sale_price: ""
  sku: "WC-EBA-01"
  weight: ""
  
  Meshes named mattress_extradetails_200x160. Match dimensions with dimensions in drop-down menu.
  Show specific nodes based on these dimensions.
  */

  const parseMeshName = (meshName) => {
    if (!meshName) return {};

    let updatedMeshName = meshName;

    // Remove "_" between "bed" and "frame" or "bed" and "structure"
    if (meshName.includes("bed") && meshName.includes("frame")) {
        updatedMeshName = meshName.replace("bed_frame", "bedframe");
    } else if (meshName.includes("bed") && meshName.includes("structure")) {
        updatedMeshName = meshName.replace("bed_structure", "bedstructure");
    }

    const parts = updatedMeshName.split("_");
    let attributes = {};
    let sizePattern = /^\d+x\d+$/;
    let sizeValue = null;
    let baseAttribute = parts[0]; // Store the first part to use for size naming

    // Loop through parts to identify size and assign attributes correctly
    // The last element of the array, unless it's a size element, is ignored as it's a value only, not a key
    for (let i = 0; i < parts.length; i++) {
        if (sizePattern.test(parts[i])) {
            sizeValue = parts[i]; // Capture the size value
            attributes[`${baseAttribute}_size`] = sizeValue; // Create "bedframe_size" attribute
        } else if (i < parts.length - 1 && !sizePattern.test(parts[i + 1])) {
            attributes[parts[i]] = parts[i + 1]; // Normal key-value assignment
            i++; // Skip next part since it's a value
        } else if (i < parts.length - 2 && sizePattern.test(parts[i + 1])) {
          attributes[parts[i]] = parts[i + 2]; // Skip size attribute
        } 
    }

    return attributes;
};


  const handleMeshClick = () => {
    if (!meshClicked || !productVariantsDetails) return;

    log('Mesh clicked:', meshClicked);
  
    setKeyForceUpdate((prev) => prev + 1);

    const parsedAttributes = parseMeshName(meshClicked.name);
    log('PARSED ATTRIBUTES', parsedAttributes);
    let attributesToAdd = [];

    let keys = Array.from(attributesMap);
    log('Available keys in attributesMap:', keys);

    for (let [attribute, value] of Object.entries(parsedAttributes)) {
      log('ATTRIBUTE MAP:', attributesMap, 'ATTRIBUTE:', attribute);

      let matchedEntry = keys.find(([key, _]) => {
        log('ATTR:', key);
        log(`Comparing "${key.toLowerCase()}" with "${attribute.toLowerCase()}"`);
        return key.toLowerCase() === attribute.toLowerCase();
      });

      if (matchedEntry) {
        let [matchedAttribute, attributeValues] = matchedEntry;

        if (attributeValues instanceof Set) {
          let valuesArray = Array.from(attributeValues);
          if (valuesArray.some(val => val.toLowerCase() === value.toLowerCase())) {
            attributesToAdd.push(matchedAttribute);
            log('Show side panel, mesh clicked:', meshClicked);
            log('Attributes to add, value:', matchedAttribute, value);
            //setSelectedAttributes(prev => ({ ...prev, [matchedAttribute]: value }));
            setSelectedValue(prev => ({ ...prev, [matchedAttribute]: value }));
          } else {
            log(`Value "${value}" not found in`, valuesArray);
          }
        } else {
          console.error(`Expected a Set, but found:`, attributeValues);
        }
      } else {
        log(`No match found for attribute: "${attribute}"`);
      }

    }
    setSelectedAttributesForPanel(attributesToAdd);
    setShowSidePanel(true);
  
    //Show selected button on side panel, even on first click (use default SKU)
    // if (meshClicked && productVariantsDetails) {
    //   for (let [attribute] of extractAttributes().entries()) {
    //     const testString = "size"; // Fake match for testing
    //     // if (attribute.toLowerCase().includes(meshClicked.toLowerCase())) {
    //     if (attribute.toLowerCase().includes(testString)) {
    //       setSelectedAttributeForPanel(attribute);
    //       setShowSidePanel(true);
    //       log('Show side panel, mesh clicked:', meshClicked);
    //       return;
    //     }
    //   }
    // }
  };

  // Ensure the function is called whenever a mesh is clicked
  useEffect(() => {
    if (meshClicked) {
      handleMeshClick();
    }
  }, [meshClicked?.id]); // Use a unique identifier to trigger updates

  useEffect(() => {
    //Store variant settings in map
    let tempProductVariantDetails = new Map();
    //Store variant settings in map
    switch (shopDatabaseType) {
      case databaseType.shopify:
        //Need to extract data from shopify to get the right structure
        //Handle productVariants here to extract relevant data when isInteractive is false and output a json struct
        //that describes the folders hierarchy to use to find the right model for the selected product features
        if (!isInteractive) {
          productVariants.attributes.forEach((item) => {
            //Get the string after pa_attribute_ 
            //Put each string in a map (tempProductVariantDetails)
            // as a key and set the value to the value of that attribute
            //e.g. "royale"
          })
        }
        //tempProductVariantDetails.push()
      default:
        break;
      case databaseType.wooCommerce:
        productVariants.forEach((item, index) => {
          tempProductVariantDetails.set(item.sku, item.attributes);
        })
        log('VARIANT MAP CONFIG:', tempProductVariantDetails);
        break;
      case databaseType.test:
        //Need test array
        // productVariants.map((item) => 
        // );
        break;
    } 

    setProductVariantsDetails(tempProductVariantDetails);
  }, [productVariants]);

  useEffect(() => {
    if (Object.keys(selectedAttributes).length > 0) {
      findMatchingSKU();
    }
  }, [selectedAttributes]);

  const findMatchingSKU = () => {
    log('Selected attributes:', selectedAttributes);
    
    for (let [sku, attributes] of productVariantsDetails.entries()) {
        log('Checking SKU:', sku, 'with attributes:', attributes);
        
        let matches = Object.keys(selectedAttributes).every(attr => {
            let attributeKey = `pa_attribute_${attr.toLowerCase()}`;
            let selectedValue = selectedAttributes[attr]?.toLowerCase(); // Convert selected value to lowercase
            let productValue = attributes[attributeKey]?.toLowerCase();  // Convert product value to lowercase

            log('Matching attribute:', attributeKey, 'Selected:', selectedValue, 'Product:', productValue);
            
            return selectedValue === productValue; // Compare in lowercase
        });

        if (matches) {
            log('Match found:', sku);
            setNewSKU(sku);
            return;
        }
    }
    
    log('No matching SKU found.');
};

  const handleSelectionChange = (attribute, value) => {
    setSelectedAttributes(prev => ({ ...prev, [attribute]: value }));
    setSelectedValue(prev => ({ ...prev, [attribute]: value }));
  };

  const onCloseSidePanel = () => {
    setShowSidePanel(false);
    onClose();
  }

  const extractAttributes = () => {
    let attributeMap = new Map();
    if(!productVariantsDetails) return attributeMap;
    productVariantsDetails.forEach(attributes => {
      Object.entries(attributes).forEach(([key, value]) => {
        if (key.startsWith('pa_attribute_')) {
          let attrName = key.replace('pa_attribute_', '');
          attrName = attrName.charAt(0).toUpperCase() + attrName.slice(1);
          if (!attributeMap.has(attrName)) {
            attributeMap.set(attrName, new Set());
          }
          attributeMap.get(attrName).add(value.charAt(0).toUpperCase() + value.slice(1));
        }
      });
    });
    return attributeMap;
  };

  const attributesMap = extractAttributes();
  log('ATTRIBUTES MAP', attributesMap);

  //selectedAttributesForPanel is now a map
  return (  
    <>
    {log('mesh clicked, selectedAttributesForPanel, showSidePanel:', meshClicked, selectedAttributesForPanel, showSidePanel)}
      {meshClicked !== null && selectedAttributesForPanel.length > 0 && showSidePanel && (
        <>
        {log('selected attributes for panel:', selectedAttributesForPanel)}
        <motion.div
          key={keyForceUpdate}
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="configurator-panel"
        >
          <div className="configurator-header">
            <h3>Customize {selectedAttributesForPanel}</h3>
            <button className="close-button-side" onClick={onCloseSidePanel}>×</button>
          </div>

          <div className="configurator-options">
            {selectedAttributesForPanel.map(attribute => {
              log('Searching for:', attribute, 'Keys in attributesMap:', [...attributesMap.keys()]);
              const values = attributesMap.get(attribute);
              log('Values found:', values);
              // TODO: Add more values in example so they're populated
              return values ? (
                <div key={attribute} className="button-group">
                  <label>{attribute}</label>
                  {Array.from(values).map(value => (
                    <button
                      key={value}
                      className={selectedValue[attribute].toLowerCase() === value.toLowerCase() ? "selected" : ""}
                      onClick={() => handleSelectionChange(attribute, value)}
                    >
                      {value}
                    </button>
                  ))}
                </div>
              ) : null;
            })}
          </div>
        </motion.div>
        </>
        )}
      {/* {attributesMap && (
        <div className="configurator-overlay">
          <div className="configurator-content">
            {Array.from(attributesMap.entries()).map(([attribute, values]) => (
              <div key={attribute} className="dropdown">
                <label>{attribute}</label>
                <select onChange={(e) => handleSelectionChange(attribute, e.target.value)}>
                  <option value="">Select {attribute}</option>
                  {Array.from(values).map((value) => (
                    <option key={value} value={value}>{value}</option>
                  ))}
                </select>
              </div>
            ))}
            <button className="close-button" onClick={onClose}>Close</button>
          </div>
        </div>
      )} */}
    </>
  );
};
export default ProductConfigurator;
